import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import RadioGroup, {
  RadioGroupItem,
} from "components/DesignSystem/RadioGroup/RadioGroup";
import { PriceInput } from "components/PriceInput/PriceInput";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Trash20 } from "components/icons/Trash20";
import {
  PRICE_FREQUENCY,
  priceFrequencyOptions,
} from "constants/productPriceFrequency";
import { FieldArray, getIn, useFormikContext } from "formik";
import { useMemo } from "react";
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Card from "components/DesignSystem/Card/Card";
import BankWarningCircle from "static/images/BankWarningCircle.svg";
import classNames from "classnames";
import { TableUI } from "components/design/TableUI";

type PriceRowItem = {
  uuid?: string;
  price: number;
  description: string;
  is_default: boolean;
  frequency: string;
};

type FormValues = {
  linked_price_items: PriceRowItem[];
};

const getDuplicatePriceIndices = (items: PriceRowItem[]) => {
  const seenPrices = new Map<number, number>();
  const duplicateIndices = new Set<number>();

  items.forEach((item, index) => {
    if (seenPrices.has(item.price)) {
      duplicateIndices.add(index);
    } else {
      seenPrices.set(item.price, index);
    }
  });

  return duplicateIndices;
};

const DefaultRadio = (info: CellContext<PriceRowItem, boolean>) => {
  return (
    <div className="t-flex t-items-center t-justify-center t-w-full">
      <RadioGroupItem value={String(info.row.index)} />
    </div>
  );
};

const AmountCell = (info: CellContext<PriceRowItem, number>) => {
  const index = info.row.index;
  const meta = info.table.options.meta as
    | {
        duplicateIndices: Set<number>;
        isDisabled?: boolean;
        hasPriceFieldError?: boolean;
      }
    | undefined;

  const isDuplicate = meta?.duplicateIndices.has(index);
  const isZeroAmount = meta?.hasPriceFieldError && info.row.original.price <= 0;
  const hasError = isDuplicate || isZeroAmount;

  return (
    <div
      className={classNames({
        "[&_.t-border]:!t-border-red-50 [&_.t-border]:hover:!t-border-red-50":
          hasError,
      })}
    >
      <PriceInput
        name={`linked_price_items.${index}.price`}
        required
        rightAlign
        disabled={meta?.isDisabled}
        rightComponent={
          isDuplicate ? (
            <img
              src={BankWarningCircle}
              alt="BankWarningCircle"
              className="t-size-5"
            />
          ) : undefined
        }
        hideError
      />
    </div>
  );
};

const DescriptionCell = (info: CellContext<PriceRowItem, string>) => {
  const index = info.row.index;
  const meta = info.table.options.meta as { isDisabled?: boolean } | undefined;

  return (
    <TextInput
      name={`linked_price_items.${index}.description`}
      placeholder="Description"
      disabled={meta?.isDisabled}
    />
  );
};

const FrequencyCell = (info: CellContext<PriceRowItem, string>) => {
  const index = info.row.index;
  const meta = info.table.options.meta as { isDisabled?: boolean } | undefined;

  return (
    <Combobox
      name={`linked_price_items.${index}.frequency`}
      options={priceFrequencyOptions}
      value={priceFrequencyOptions.find(
        (opt) => opt.value === info.row.original.frequency
      )}
      withForm
      placeholder="Select"
      isClearable
      isDisabled={meta?.isDisabled}
      menuPortalTarget={document.body}
    />
  );
};

const DeleteCell = ({
  info,
  onRemove,
}: {
  info: CellContext<PriceRowItem, unknown>;
  onRemove: (index: number) => void;
}) => {
  const canDelete = info.table.options.data.length > 1;
  const meta = info.table.options.meta as { isDisabled?: boolean } | undefined;

  return (
    <Button
      customType="ghost_icon"
      size="small"
      type="button"
      onClick={() => onRemove(info.row.index)}
      disabled={!canDelete || meta?.isDisabled}
    >
      <span className="t-text-text-30">
        <Trash20 />
      </span>
    </Button>
  );
};

export const PriceRowTable = ({ isDisabled }: { isDisabled?: boolean }) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormValues>();
  const { linked_price_items } = values;

  const defaultIndex = linked_price_items.findIndex((item) => item.is_default);
  const duplicateIndices = getDuplicatePriceIndices(linked_price_items);
  const linkedPriceErrors = getIn(errors, "linked_price_items");
  const isTouched = getIn(touched, "linked_price_items");

  const hasDuplicateError = isTouched && typeof linkedPriceErrors === "string";
  const hasPriceFieldError =
    isTouched &&
    Array.isArray(linkedPriceErrors) &&
    linkedPriceErrors.some((err) => err?.price);

  const onDefaultChange = (value: string) => {
    const newDefaultIndex = Number(value);
    linked_price_items.forEach((_, index) => {
      setFieldValue(
        `linked_price_items.${index}.is_default`,
        index === newDefaultIndex
      );
    });
  };

  const handleRemove = (index: number) => {
    const wasDefault = linked_price_items[index].is_default;
    const newItems = linked_price_items.filter((_, i) => i !== index);

    if (wasDefault && newItems.length > 0) {
      newItems[0].is_default = true;
    }

    setFieldValue("linked_price_items", newItems);
  };

  const createColumn = createColumnHelper<PriceRowItem>();

  const columns = useMemo(
    () => [
      createColumn.accessor("is_default", {
        size: 12,
        header: () => (
          <HeadCellContent title="Mark as default" align="center" />
        ),
        cell: DefaultRadio,
      }),
      createColumn.accessor("price", {
        size: 15,
        header: () => <HeadCellContent title="Amount" align="left" required />,
        cell: AmountCell,
      }),
      createColumn.accessor("description", {
        size: 46,
        header: () => <HeadCellContent title="Description" />,
        cell: DescriptionCell,
      }),
      createColumn.accessor("frequency", {
        size: 20,
        header: () => <HeadCellContent title="Frequency" />,
        cell: FrequencyCell,
      }),
      createColumn.display({
        id: "delete",
        size: 7,
        header: "",
        cell: (info) => <DeleteCell info={info} onRemove={handleRemove} />,
      }),
    ],
    [linked_price_items.length, handleRemove]
  );

  const table = useReactTable({
    data: linked_price_items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
    meta: {
      isDisabled,
      duplicateIndices,
      hasPriceFieldError,
    },
  });

  return (
    <Card.Root className="!t-overflow-visible sm:!t-px-3">
      <Card.Header>
        <Card.Title>Prices</Card.Title>
      </Card.Header>
      <Card.Body>
        <FieldArray name="linked_price_items">
          {({ push }) => (
            <RadioGroup.Root
              value={String(defaultIndex)}
              onValueChange={onDefaultChange}
              disabled={isDisabled}
            >
              <TableUI
                table={table}
                sticky={false}
                isHeaderCellUpperCase={false}
                layout="fixed"
                contentClassName="sm:t-min-w-[640px]"
              />
              {hasDuplicateError && (
                <p className="t-text-red t-text-body-sm t-m-0 t-pb-3 t-pt-2">
                  {linkedPriceErrors}
                </p>
              )}
              {hasPriceFieldError && !hasDuplicateError && (
                <p className="t-text-red t-text-body-sm t-m-0 t-pb-3 t-pt-2">
                  Amount must be greater than 0
                </p>
              )}
              {!isDisabled && (
                <div className="t-pt-4">
                  <Button
                    size="small"
                    type="button"
                    onClick={() =>
                      push({
                        price: 0,
                        description: "",
                        is_default: false,
                        frequency: PRICE_FREQUENCY.ONE_TIME,
                      })
                    }
                  >
                    Add more
                  </Button>
                </div>
              )}
            </RadioGroup.Root>
          )}
        </FieldArray>
      </Card.Body>
    </Card.Root>
  );
};
